import React from "react";
import { PageNavigation } from "../../../../components/common/PageNavigation";

type Props = {
  showSidebar: boolean;
  backLabel: string;
  backTo: string;
};

const SkeletonBlock: React.FC<{ className: string }> = ({ className }) => (
  <div
    className={`${className} animate-pulse [animation-duration:2.5s]`}
    style={{ background: "var(--brand-skeleton-base, #e5e7eb)" }}
  />
);

export const HousingComplexSkeleton: React.FC<Props> = ({ showSidebar, backLabel, backTo }) => (
  <div
    className="mx-auto flex w-full max-w-[1440px] flex-col px-4 pb-12 pt-4 sm:px-5 lg:px-8 lg:pb-16"
    aria-busy="true"
    aria-label="Загружаем объект"
    role="status"
  >
    <PageNavigation backLabel={backLabel} backTo={backTo} className="mb-5" />

    <div className="space-y-6">
      <section
        className="overflow-hidden rounded-[28px] border"
        style={{
          borderColor: "var(--brand-border, #e7ebf0)",
          background: "var(--brand-surface, #ffffff)",
        }}
      >
        <div className="grid gap-0 xl:grid-cols-[minmax(0,1.25fr)_minmax(340px,0.75fr)]">
          <SkeletonBlock className="aspect-square min-h-[320px] w-full rounded-none xl:aspect-[0.94/1]" />

          <div className="flex flex-col p-5 sm:p-6 lg:p-8">
            <SkeletonBlock className="h-3 w-36 rounded-full" />
            <div className="mt-4 grid gap-3">
              <SkeletonBlock className="h-10 w-4/5 max-w-[420px] rounded-xl" />
              <SkeletonBlock className="h-10 w-3/5 max-w-[320px] rounded-xl" />
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="rounded-[16px] border p-4"
                  style={{ borderColor: "var(--brand-border, #edf1f5)" }}
                >
                  <SkeletonBlock className="h-3 w-20 rounded-full" />
                  <SkeletonBlock className="mt-3 h-5 w-4/5 rounded-md" />
                </div>
              ))}
            </div>

            <div
              className="mt-7 border-t pt-6"
              style={{ borderColor: "var(--brand-border, #edf1f5)" }}
            >
              <SkeletonBlock className="h-3 w-24 rounded-full" />
              <div className="mt-4 grid gap-3">
                <SkeletonBlock className="h-4 w-full rounded-md" />
                <SkeletonBlock className="h-4 w-11/12 rounded-md" />
                <SkeletonBlock className="h-4 w-4/5 rounded-md" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div
        className={`grid gap-6 ${showSidebar ? "xl:grid-cols-[minmax(0,1fr)_320px] xl:items-start" : ""}`.trim()}
      >
        <section
          className="min-h-[420px] rounded-[28px] border p-5 sm:p-6"
          style={{
            borderColor: "var(--brand-border, #e7ebf0)",
            background: "var(--brand-surface, #ffffff)",
          }}
        >
          <SkeletonBlock className="h-3 w-28 rounded-full" />
          <SkeletonBlock className="mt-4 h-9 w-64 max-w-full rounded-xl" />
          <div
            className="mt-6 grid gap-4 border-t pt-6 sm:grid-cols-2"
            style={{ borderColor: "var(--brand-border, #edf1f5)" }}
          >
            {Array.from({ length: 4 }).map((_, index) => (
              <SkeletonBlock key={index} className="h-20 rounded-[16px]" />
            ))}
          </div>
          <SkeletonBlock className="mt-6 h-12 w-44 rounded-[14px]" />
        </section>

        {showSidebar ? (
          <aside className="hidden space-y-4 xl:block">
            <section
              className="rounded-[24px] border p-5"
              style={{
                borderColor: "var(--brand-border, #e7ebf0)",
                background: "var(--brand-surface, #ffffff)",
              }}
            >
              <SkeletonBlock className="h-3 w-28 rounded-full" />
              <SkeletonBlock className="mt-4 h-8 w-48 rounded-lg" />
              <SkeletonBlock className="mt-6 h-11 w-full rounded-[18px]" />
              <SkeletonBlock className="mt-3 h-11 w-full rounded-[18px]" />
            </section>

            <section
              className="rounded-[24px] border p-5"
              style={{
                borderColor: "var(--brand-border, #e7ebf0)",
                background: "var(--brand-surface, #ffffff)",
              }}
            >
              <SkeletonBlock className="h-3 w-32 rounded-full" />
              <div className="mt-5 grid gap-4">
                <SkeletonBlock className="h-14 rounded-lg" />
                <SkeletonBlock className="h-14 rounded-lg" />
                <SkeletonBlock className="h-14 rounded-lg" />
              </div>
            </section>
          </aside>
        ) : null}
      </div>
    </div>
  </div>
);
